<template>
  <div class="new-home-container">
    <h1 class="mb-3">New Config Manager</h1>
    <!-- Navigation button to old home page -->
    <div class="mb-4">
      <router-link :to="{ name: 'OldHome' }" class="btn-old-home">
        Go to Classic View
      </router-link>
    </div>
    
    <!-- Favorites Section -->
    <div class="mb-4" v-if="favoriteApps.length > 0">
      <h2 style="font-size: 1.5rem; font-weight: 600;">Favorites</h2>
      <div class="cards-grid">
        <div 
          class="card-container"
          :class="{ 'card-animation': isAnimating && animatingAppKey === app.key }"
          v-for="app in favoriteApps" 
          :key="app.key"
        >
          <router-link 
            :to="{name: 'Parameters', params:{aid: app.key}}"
            class="card-link"
          >
            <div class="glassmorphism-card">  
              <div class="card-content">
                <h3 class="card-title">{{ app.value.appname }}</h3>
                <p class="card-subtitle">{{ app.key }}</p>
              </div>
            </div>
          </router-link>
          <div class="favorite-icon active" @click.prevent="animateToggleFavorite(app.key)">
            <i class="tim-icons icon-heart-2"></i>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Toggle Switch -->
    <div class="toggle-container mb-4">
      <div class="status-toggle">
        <button 
          class="toggle-btn" 
          :class="{ active: selectedStatus === 'active' }"
          @click="selectedStatus = 'active'"
        >
          Active Apps
        </button>
        <button 
          class="toggle-btn" 
          :class="{ active: selectedStatus === 'inactive' }"
          @click="selectedStatus = 'inactive'"
        >
          Inactive Apps
        </button>
      </div>
    </div>
    
    <div>
      <h2 style="font-size: 1.5rem; font-weight: 600;">{{ selectedStatus.charAt(0).toUpperCase() + selectedStatus.slice(1) + ' Apps' }}</h2>
      <div class="cards-grid">
        <div 
          class="card-container"
          :class="{ 'card-animation': isAnimating && animatingAppKey === app.key }"
          v-for="app in apps[selectedStatus]" 
          :key="app.key"
        >
          <router-link 
            :to="{name: 'Parameters', params:{aid: app.key}}"
            class="card-link"
          >
            <div class="glassmorphism-card">  
              <div class="card-content">
                <h3 class="card-title">{{ app.value.appname }}</h3>
                <p class="card-subtitle">{{ app.key }}</p>
              </div>
            </div>
          </router-link>
          <div 
            class="favorite-icon" 
            :class="{ active: isFavorite(app.key) }" 
            @click.prevent="animateToggleFavorite(app.key)"
          >
            <i class="tim-icons icon-heart-2"></i>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      statuses: ['active','inactive'],
      selectedStatus: 'active',
      favorites: [],
      isAnimating: false,
      animatingAppKey: null
    }
  },
  created() {
    // Load favorites from localStorage
    this.loadFavorites();
  },
  computed: {
    apps() {
      const allApps = this.$store.getters.appsDetails
      let apps = {active: [], inactive: []}
      for (const [key, value] of Object.entries(allApps)) {
        if (value.status) {
          apps.active.push({key, value})
        } else {
          apps.inactive.push({key, value})
        }
      }
      return apps
    },
    favoriteApps() {
      const allApps = this.$store.getters.appsDetails;
      return this.favorites
        .map(favKey => {
          if (allApps[favKey]) {
            return {
              key: favKey,
              value: allApps[favKey]
            };
          }
          return null;
        })
        .filter(app => app !== null);
    }
  },
  methods: {
    loadFavorites() {
      try {
        const storedFavorites = localStorage.getItem('favoriteApps');
        this.favorites = storedFavorites ? JSON.parse(storedFavorites) : [];
      } catch (error) {
        console.error('Error loading favorites from localStorage:', error);
        this.favorites = [];
      }
    },
    saveFavorites() {
      try {
        localStorage.setItem('favoriteApps', JSON.stringify(this.favorites));
      } catch (error) {
        console.error('Error saving favorites to localStorage:', error);
      }
    },
    toggleFavorite(appKey) {
      const index = this.favorites.indexOf(appKey);
      if (index === -1) {
        // Add to favorites
        this.favorites.push(appKey);
      } else {
        // Remove from favorites
        this.favorites.splice(index, 1);
      }
      this.saveFavorites();
    },
    animateToggleFavorite(appKey) {
      this.isAnimating = true;
      this.animatingAppKey = appKey;
      
      // Wait for animation to complete before toggling the favorite
      setTimeout(() => {
        this.toggleFavorite(appKey);
        
        // Reset animation state after a delay
        setTimeout(() => {
          this.isAnimating = false;
          this.animatingAppKey = null;
        }, 300);
      }, 300);
    },
    isFavorite(appKey) {
      return this.favorites.includes(appKey);
    }
  }
}
</script>

<style scoped>
.btn-old-home {
  display: inline-block;
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  text-decoration: none;
  transition: all 0.3s ease;
  font-weight: 500;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.btn-old-home:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.toggle-container {
  display: flex;
  justify-content: center;
  align-items: center;
}

.status-toggle {
  display: flex;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 30px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
}

.toggle-btn {
  padding: 0.5rem 1.25rem;
  border: none;
  background: transparent;
  color: rgba(221, 221, 221, 0.7);
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
  font-size: 0.9rem;
  outline: none;
}

.toggle-btn:focus {
  outline: none;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.3);
}

.toggle-btn.active {
  background: rgba(255, 255, 255, 0.25);
  color: white;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.toggle-btn:hover:not(.active) {
  background: rgba(255, 255, 255, 0.15);
  color: rgba(255, 255, 255, 0.9);
}

.toggle-btn.active:focus {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1), 0 0 0 2px rgba(255, 255, 255, 0.5);
}

.new-home-container {
  width: 100%;
  max-width: 100%;
  padding: 0;
}

.cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1rem;
  width: 100%;
}

.card-container {
  position: relative;
  height: 120px;
  transition: all 0.3s ease;
  width: 100%;
}

.card-container:hover {
  transform: translateY(-5px);
}

.card-container:hover .glassmorphism-card {
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  border-color: rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.25);
}

.card-link {
  text-decoration: none;
  color: inherit;
  display: block;
  height: 100%;
  width: 100%;
}

.glassmorphism-card {
  cursor: pointer;
  height: 100%;
  width: 100%;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.glassmorphism-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 100%;
  background: linear-gradient(135deg, 
                             rgba(255, 255, 255, 0.2) 0%, 
                             rgba(255, 255, 255, 0.05) 50%, 
                             rgba(255, 255, 255, 0.02) 100%);
  z-index: 0;
}

.card-content {
  padding: 1.5rem;
  position: relative;
  z-index: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.card-title {
  font-weight: 700;
  font-size: 1.25rem;
  color: rgba(221, 221, 221, 0.8);
  margin-bottom: 0.5rem;
  text-shadow: 0 1px 2px rgba(255, 255, 255, 0.3);
}

.card-subtitle {
  font-size: 0.875rem;
  color: rgba(221, 221, 221, 0.8);
  text-shadow: 0 1px 1px rgba(255, 255, 255, 0.2);
}

.favorite-icon {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  z-index: 10;
  transition: all 0.3s ease;
}

.favorite-icon:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.1);
}

.favorite-icon.active {
  background: rgba(255, 103, 123, 0.6);
  color: white;
}

.favorite-icon.active:hover {
  background: rgba(255, 103, 123, 0.8);
}

@keyframes card-animation {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(0.9);
  }
  100% {
    transform: scale(1);
  }
}

.card-animation {
  animation: card-animation 0.6s ease;
  transform-origin: center;
}

/* Responsive design for different screen sizes */
@media (min-width: 1200px) {
  .cards-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (min-width: 768px) and (max-width: 1199px) {
  .cards-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 767px) {
  .cards-grid {
    grid-template-columns: 1fr;
  }
}
</style> 